import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AUTH_TOKEN } from "constants/AuthConstant";
import LoanActivation from "services/Setup/LoanOrigination/LoanActivation/loanActivationService";
import { notification } from "antd";

const getErrorPayload = (error) => {
  return {
    status: error?.response?.status || "Error",
    message:
      error?.response?.data?.message ||
      error?.response?.statusText ||
      "Something went wrong",
  };
};

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token:
    typeof window !== "undefined" ? localStorage.getItem(AUTH_TOKEN) : null,

  result: {},
  activationList: [],
  refreshList: false,
  getPageSize: "",
  getPageIndex: "",
  getCount: "",
  editIncentiveList: [],
  asuransiJiwaAmount: 0,
  statusRespon: 0,
};

// ✅ GET DATA
export const getAddIncentive = createAsyncThunk(
  "loanActivation/getAddIncentive",
  async (userInput, { rejectWithValue }) => {
    try {
      const response =
        await LoanActivation.getLoanActivationService(userInput);

      if (response?.status === 200) {
        return response.data;
      }

      return rejectWithValue({
        message: response?.data?.message || "Unexpected response",
      });
    } catch (error) {
      const err = getErrorPayload(error);

      notification.error({
        message: err.status,
        description: err.message,
        duration: 3,
      });

      return rejectWithValue(err);
    }
  }
);

// ✅ ADD DATA
export const addLoanActivationService = createAsyncThunk(
  "loanActivation/addLoanActivationService",
  async (userInput, { rejectWithValue }) => {
    try {
      const response =
        await LoanActivation.addLoanActivationService(userInput);

      if (response?.status === 200) {
        notification.success({
          message: "Success",
          description:
            response?.data?.message ||
            "Loan activation added successfully!",
          duration: 3,
        });

        return response.data;
      }

      notification.error({
        message: "Error",
        description: response?.data?.message || "Unexpected response!",
        duration: 3,
      });

      return rejectWithValue({
        message: response?.data?.message || "Unexpected response!",
      });
    } catch (error) {
      const err = getErrorPayload(error);

      notification.error({
        message: err.status,
        description: err.message,
        duration: 3,
      });

      return rejectWithValue(err);
    }
  }
);

export const LoanActivationSlice = createSlice({
  name: "loanActivation",
  initialState,

  reducers: {
    showNotification: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },

    hideNotification: (state) => {
      state.message = "";
      state.showMessage = false;
    },

    showLoading: (state) => {
      state.loading = true;
    },

    resetStatusRespon: (state) => {
      state.statusRespon = 0;
    },
  },

  extraReducers: (builder) => {
    builder

      // 🔹 GET
      .addCase(getAddIncentive.pending, (state) => {
        state.loading = true;
        state.showMessage = false;
      })

      .addCase(getAddIncentive.fulfilled, (state, action) => {
        state.loading = false;
        state.activationList = action.payload;
        state.statusRespon = 200;
      })

      .addCase(getAddIncentive.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload?.message || "Failed to fetch data";
        state.showMessage = true;
        state.statusRespon = action.payload?.status || 500;
      })

      // 🔹 ADD
      .addCase(addLoanActivationService.pending, (state) => {
        state.loading = true;
        state.showMessage = false;
      })

      .addCase(addLoanActivationService.fulfilled, (state, action) => {
        state.loading = false;
        state.message =
          action.payload?.message ||
          "Loan activation added successfully";
        state.showMessage = true;
        state.statusRespon = 200;
      })

      .addCase(addLoanActivationService.rejected, (state, action) => {
        state.loading = false;
        state.message =
          action.payload?.message || "Failed to add loan activation";
        state.showMessage = true;
        state.statusRespon = action.payload?.status || 500;
      });
  },
});

export const {
  showNotification,
  hideNotification,
  showLoading,
  resetStatusRespon,
} = LoanActivationSlice.actions;

export default LoanActivationSlice.reducer;